import Link from 'next/link'
import { airconCostCalculation } from '../../../js/aircon-cost'
import { HowStringsSGWorksPage } from '@/index/HowStringsSGWorks'

/**
 * Aircon trust page. Shared structure and voice: components/index/HowStringsSGWorks.js.
 * Guides carry a company byline, so the guides and locality pages link here
 * (the guides link #guides).
 *
 * Aircon-specific sources - keep claims to these:
 *  - standard steps: the 6-step general servicing process on the hub
 *    (AirconGeneralServiceProcess) and the chemical wash/overhaul process pages
 *  - data-driven pricing: /sg/aircon-servicing-singapore/the-secrets-behind-affordable-aircon-servicing-with-stringssg
 *  - prices: airconCostCalculation, the same source as the price tables
 *  - warranty: /terms/warranty-claims (30 days general/repair, 60 days
 *    chemical wash/overhaul/drainage, 90 days refrigerant gas)
 */

// When this page's content last actually changed. Bump it by hand when you edit the copy.
const CONTENT_REVIEWED = '2026-09-27'

export async function getStaticProps() {
    const annualServicing = [], standardServicing = [], chemicalServicing = [], overhaulServicing = []
    airconCostCalculation('SGD', annualServicing, standardServicing, chemicalServicing, overhaulServicing)

    const rate = (rows, units) => Number(rows[units - 1]?.rate).toFixed(2)

    return {
        props: {
            prices: {
                general1: rate(standardServicing, 1),
                general5: rate(standardServicing, 5),
                wash1: rate(chemicalServicing, 1),
                overhaul1: rate(overhaulServicing, 1),
            },
        },
        revalidate: 259200,
    }
}

const link = (href, label) => (
    <Link href={href} prefetch={false} className="text-orange-600 hover:underline">{label}</Link>
)

export default function HowStringsSGWorksAircon({ prices }) {
    return (
        <HowStringsSGWorksPage
            path="/sg/aircon-servicing-singapore/how-stringssg-works"
            title="How StringsSG Aircon Servicing Works | Our Service Standards"
            h1="How StringsSG Aircon Servicing Works"
            description="StringsSG defines the service steps and quality standards for every aircon job and takes responsibility for the quality. How we vet technicians, set transport-inclusive prices, our warranty, and how our aircon guides are written."
            reviewed={CONTENT_REVIEWED}
            intro="StringsSG provides aircon servicing, chemical cleaning, gas top-up and repairs across Singapore. We set how every job is done and take responsibility for the result. This page explains our service standards, what we take care of, how our prices are set, and what happens if something is not right."
            serviceNoun="aircon job"
            people="technicians"
            vetting="we vet them and check the licences of the ACRA-registered businesses they work for"
            standards={
                <p>
                    Every general service follows the same six steps: cleaning the air filters and covers, cleaning the
                    evaporator coil, clearing the drain pan, vacuuming the drainage pipe, checking the outdoor unit, and
                    checking the refrigerant gas level. Chemical wash and chemical overhaul follow their own defined
                    processes. See{' '}
                    {link('/sg/aircon-servicing-singapore/cleaning-maintenance-service', 'general aircon servicing')} and{' '}
                    {link('/sg/aircon-servicing-singapore/aircon-chemical-wash-and-overhaul', 'chemical wash and overhaul')}.
                </p>
            }
            pricing={
                <>
                    <p>
                        Running bookings, scheduling, invoicing and supplies centrally removes cost from every job, and
                        that is what keeps our published prices low. Prices are set from StringsSG&apos;s own booking data,
                        collected since 2018, and are published up front by number of units, with transport already
                        included - there is no separate callout or transport fee.
                    </p>
                    <p>
                        General servicing is ${prices.general1} for one unit and ${prices.general5} for five units.
                        A chemical wash starts at ${prices.wash1} per unit and a chemical overhaul at ${prices.overhaul1} per
                        unit. You see the final price before you confirm the booking.{' '}
                        {link('/sg/aircon-servicing-singapore/aircon-servicing-price-singapore', 'Full aircon servicing price list')}
                    </p>
                </>
            }
            afterService={
                <p>
                    General servicing and repair carry a 30-day workmanship warranty; chemical wash, chemical overhaul
                    and drainage work carry 60 days; refrigerant gas top-up carries 90 days. If an issue related to or
                    caused by the service arises within that period, contact us through the app or on WhatsApp and we
                    resolve it at no additional charge. Parts and installed items carry the manufacturer&apos;s warranty.{' '}
                    {link('/terms/warranty-claims', 'Warranty terms')}
                </p>
            }
            extraSections={[
                {
                    // id is linked from every aircon guide ("How our guides are written").
                    id: 'guides',
                    title: 'How our aircon guides are written',
                    body: (
                        <>
                            <p>
                                Our aircon guides are written by the StringsSG team, drawing on the jobs our technicians
                                carry out and on StringsSG&apos;s own booking data. Figures such as the job statistics on our
                                locality pages come straight from our booking records, are rounded, and are published only
                                where there are enough bookings behind them. Prices quoted are StringsSG&apos;s published
                                prices at the time of writing.
                            </p>
                            <p>
                                If you spot something out of date or wrong in a guide, tell us on WhatsApp and we will
                                correct it. {link('/sg/blogs/hvac-services', 'Browse our aircon guides')}
                            </p>
                        </>
                    ),
                },
            ]}
            bookHref="/sg/aircon-servicing-singapore"
            bookLabel="Book aircon servicing"
        />
    )
}
